import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
export const Route = createFileRoute('/_layout/learn/smart-links/')({
    component: LearnSmartLinksPage,
    head: () => ({
        meta: [
            { title: 'Smart Links — How They Work & When to Use Them | Adora' },
            {
                name: 'description',
                content: 'Smart Links track how people arrive, not just where they go. Learn how Adora Smart Links differ from ordinary redirects and link-in-bio tools.',
            },
            { property: 'og:title', content: 'Smart Links — How They Work & When to Use Them | Adora' },
            {
                property: 'og:description',
                content: 'Smart Links track how people arrive, not just where they go. Learn how Adora Smart Links differ from ordinary redirects and link-in-bio tools.',
            },
        ],
    }),
});
function LearnSmartLinksPage() {
    return (<article className="mx-auto max-w-5xl px-6 md:px-8 py-12 space-y-14">
            <Intro />
            <WhySmartLinks />
            <WhatMakesSmartLinkDifferent />
            <SmartLinkVsLinkInBio />
            <WhenToUseSmartLinks />
            <NextSteps />
        </article>);
}
/* ------------------------------ */
/* Intro */
/* ------------------------------ */
function Intro() {
    return (<section className="space-y-4">
            <Badge variant="secondary">Learn</Badge>

            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Smart Links</h1>

            <p className="max-w-2xl text-muted-foreground">
                Smart Links help you understand <strong>how</strong> people arrive, not just
                <strong> where</strong> they go.
            </p>

            <p className="max-w-2xl text-muted-foreground">
                They are designed for campaigns, comparisons, and real-world distribution — not just collecting links on
                a page.
            </p>
        </section>);
}
/* ------------------------------ */
/* Why Smart Links exist */
/* ------------------------------ */
function WhySmartLinks() {
    return (<section className="space-y-6">
            <h2 className="text-2xl font-semibold">Why Smart Links exist</h2>

            <div className="grid gap-4 md:grid-cols-3">
                {[
            {
                title: 'Links hide context',
                text: 'Normal links don’t tell you why someone clicked or where they came from.',
            },
            {
                title: 'QR codes lack insight',
                text: 'Most QR tools only count scans, not intent or next actions.',
            },
            {
                title: 'Offline is invisible',
                text: 'Flyers, packaging, menus, and posters rarely get measured properly.',
            },
        ].map((item) => (<Card key={item.title}>
                        <CardContent className="p-5 space-y-2">
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-muted-foreground">{item.text}</p>
                        </CardContent>
                    </Card>))}
            </div>
        </section>);
}
/* ------------------------------ */
/* What makes a Smart Link */
/* ------------------------------ */
function WhatMakesSmartLinkDifferent() {
    return (<section className="space-y-6">
            <h2 className="text-2xl font-semibold">What makes a Smart Link “smart”</h2>

            <Card>
                <CardContent className="p-6 space-y-4 text-muted-foreground">
                    <p>
                        A Smart Link is not just a redirect. It’s a <strong>measurement layer</strong>.
                    </p>

                    <ul className="list-disc pl-5 space-y-1">
                        <li>Tracks when people interact</li>
                        <li>Tracks basic location and context</li>
                        <li>Tracks what people click next</li>
                        <li>Separates entry point from destination</li>
                    </ul>

                    <p>
                        This allows you to compare placements, messages, and formats — without changing your
                        destination.
                    </p>
                </CardContent>
            </Card>
        </section>);
}
/* ------------------------------ */
/* Smart Link vs Link-in-bio */
/* ------------------------------ */
function SmartLinkVsLinkInBio() {
    return (<section className="space-y-6">
            <h2 className="text-2xl font-semibold">Smart Links vs link-in-bio tools</h2>

            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Link-in-bio</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>• Groups multiple links</p>
                        <p>• Designed for profiles</p>
                        <p>• Focuses on presentation</p>
                        <p>• Limited insight into intent</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Smart Links</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>• Tracks entry context</p>
                        <p>• Designed for campaigns</p>
                        <p>• Measures next actions</p>
                        <p>• Built for comparison and learning</p>
                    </CardContent>
                </Card>
            </div>

            <p className="text-sm text-muted-foreground">
                Smart Links can power a landing page — but their real value is what they measure, not how they look.
            </p>
        </section>);
}
/* ------------------------------ */
/* When to use Smart Links */
/* ------------------------------ */
function WhenToUseSmartLinks() {
    return (<section className="space-y-6">
            <h2 className="text-2xl font-semibold">When Smart Links make sense</h2>

            <div className="grid gap-4 sm:grid-cols-2">
                {[
            'Flyers and posters',
            'Menus and table tents',
            'Packaging and labels',
            'Events and pop-ups',
            'Bio links with tracking',
            'Offline experiments',
        ].map((item) => (<Card key={item}>
                        <CardContent className="p-4 text-sm">{item}</CardContent>
                    </Card>))}
            </div>

            <p className="text-sm text-muted-foreground max-w-2xl">
                If you care about <strong>why</strong> people click — not just <strong>how many</strong> — Smart Links
                are the right tool.
            </p>
        </section>);
}
/* ------------------------------ */
/* Next steps */
/* ------------------------------ */
function NextSteps() {
    return (<section className="space-y-6">
            <Separator />

            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardContent className="p-5 space-y-2">
                        <p className="font-medium">Deep dive</p>
                        <Link to="/smart-link" className="text-sm text-primary hover:underline">
                            What is an Adora Smart Link →
                        </Link>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-5 space-y-2">
                        <p className="font-medium">Compare</p>
                        <Link to="/learn/smart-links-vs-link-in-bio" className="text-sm text-primary hover:underline">
                            Smart Links vs link-in-bio →
                        </Link>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-5 space-y-2">
                        <p className="font-medium">Context</p>
                        <Link to="/learn/offline-marketing-analytics" className="text-sm text-primary hover:underline">
                            Offline marketing analytics →
                        </Link>
                    </CardContent>
                </Card>
            </div>
        </section>);
}
